/** Adoption definitions and DTOs. These are observations, not learning scores. */
export const PRODUCT_ANALYTICS_DEFINITION_VERSION = 1 as const;
export const PRODUCT_ANALYTICS_MAX_TIME_MS = 20_000;
export const PRODUCT_ANALYTICS_DEFAULT_THRESHOLD = 5;
/** One audited exclusion command carries at most one candidate page of targets. */
export const PRODUCT_ANALYTICS_EXCLUSION_BATCH_MAX = 100;
export const PRODUCT_ANALYTICS_SOURCES = [
  'practiceActivity',
  'practiceProvenance',
  'homeworkActivity',
  'publicationRoster',
  'homeworkTime',
  'publicDemo',
  'printHistory',
  'teacherEvents',
] as const;
export type AnalyticsSource = (typeof PRODUCT_ANALYTICS_SOURCES)[number];
/**
 * Sources whose collectors shipped after a tenant's readiness stamp
 * (`indexesReadyAt`). That stamp says nothing about them, so their readiness
 * is the operator's verified complete-capture boundary itself: until
 * `completeFrom` is recorded their metrics are unavailable, never a partial
 * count of an unverified stream.
 */
export const STAMP_READY_ANALYTICS_SOURCES: readonly AnalyticsSource[] = [
  'teacherEvents',
];
/** A deliberate action counts toward a class started at most this long after it. */
export const NIGHT_BEFORE_WINDOW_HOURS = 24;
/** A logged nudge makes a tutor's classes starting within this long after it prompted. */
export const NUDGE_LOOKBACK_HOURS = 48;
/** A copy is timed from a class page open only when it follows within this long. */
export const COPY_AFTER_OPEN_WINDOW_MINUTES = 30;
export type AnalyticsPopulation = 'regular' | 'nonprofit' | 'all';
export type AnalyticsPopulationSelection = AnalyticsPopulation | 'compare';
export type AnalyticsSelectionCategory =
  | 'system_picked'
  | 'student_selected'
  | 'unclassified';
export type AnalyticsMetricStatus =
  | 'available'
  | 'partial'
  | 'unavailable'
  | 'not-yet-observable'
  | 'no-eligible-population';
export type AnalyticsCountedUnit =
  | 'students'
  | 'teachers'
  | 'answers'
  | 'materials'
  | 'delivery_actions'
  | 'student_homework_pairs'
  | 'runs'
  | 'estimated_browsers'
  | 'class_occurrences'
  | 'copy_actions'
  | 'page_opens'
  | 'nudges'
  | 'minutes'
  | 'photos'
  | 'percent';

export const PRODUCT_ANALYTICS_METRICS = {
  meaningful_practice: {
    label: 'Meaningful practice',
    unit: 'students',
    definition:
      'Students answering at least the selected number of eligible voluntary practice questions in one Singapore day, across sessions and selection modes. Incorrect answers count.',
  },
  practice_habit: {
    label: 'Practice habit',
    unit: 'students',
    definition:
      'Students with at least three meaningful practice days in the seven reporting days ending on the reference day.',
  },
  dau: {
    label: 'Active students · 1 day',
    unit: 'students',
    definition:
      'Distinct students with an eligible practice answer or a genuine homework answer on the reference day.',
  },
  wau: {
    label: 'Active students · 7 days',
    unit: 'students',
    definition:
      'Distinct union of active students across the seven reporting days ending on the reference day.',
  },
  mau: {
    label: 'Active students · 30 days',
    unit: 'students',
    definition:
      'Distinct union of active students across the 30 reporting days ending on the reference day.',
  },
  practice_active: {
    label: 'Practice active · 7 days',
    unit: 'students',
    definition:
      'Distinct students with an eligible practice answer in the trailing seven reporting days. Source counts may overlap.',
  },
  homework_active: {
    label: 'Homework active · 7 days',
    unit: 'students',
    definition:
      'Distinct students with genuine, changed, usable homework answers in the trailing seven reporting days. Opens and viewing time do not count.',
  },
  activations: {
    label: 'First meaningful practice',
    unit: 'students',
    definition:
      'Students whose first qualifying practice day in available durable history falls in this range. Recomputed for the selected threshold.',
  },
  never_qualified: {
    label: 'Never reached the practice threshold',
    unit: 'students',
    definition:
      'Tenant-linked students with no meaningful practice day anywhere in available history, including students with no answers.',
  },
  lapsed: {
    label: 'No activity in 14 days',
    unit: 'students',
    definition:
      'Students with prior eligible practice or homework activity and none in the last 14 completed Singapore days relative to the selected endpoint.',
  },
  materials_created: {
    label: 'Currently nonempty material created',
    unit: 'materials',
    definition:
      'Homework and worksheets created in the range that currently contain at least one question, including archived material. Later removal can change this historical count.',
  },
  creating_teachers: {
    label: 'Teachers creating material',
    unit: 'teachers',
    definition:
      'Distinct creators of currently nonempty homework or worksheets created in this range.',
  },
  delivery_actions: {
    label: 'Delivery actions',
    unit: 'delivery_actions',
    definition:
      'First homework publications by the actual publisher plus nonempty immutable Print / Save PDF requests in the selected range.',
  },
  delivering_teachers: {
    label: 'Teachers delivering',
    unit: 'teachers',
    definition:
      'Distinct teachers publishing homework or requesting a nonempty worksheet Print / Save PDF in the selected range. Requests do not prove a print or save completed.',
  },
  homework_publications: {
    label: 'Homework published',
    unit: 'delivery_actions',
    definition:
      'First homework publications in the selected range, attributed to the captured publisher, never reconstructed from today’s teacher.',
  },
  worksheet_prints: {
    label: 'Print / Save PDF requests',
    unit: 'delivery_actions',
    definition:
      'Immutable worksheet print requests in the range whose captured snapshot contains a question.',
  },
  night_before_teachers: {
    label: 'Mainstream tutors acting the night before',
    unit: 'percent',
    definition: `Share of mainstream tutors with a class starting in the selected range (the tutor of record of a mainstream live class session: its main tutor, or the relief tutor who covered it) who, acting as themselves, opened a student page, copied a Remind or opened a submission in the ${NIGHT_BEFORE_WINDOW_HOURS} hours before one of their classes. Each action is judged against the class start recorded with it.`,
  },
  unprompted_night_before_teachers: {
    label: 'Mainstream tutors acting the night before, unprompted',
    unit: 'percent',
    definition: `The same share, counting only actions before a class with no logged nudge for that tutor in the ${NUDGE_LOOKBACK_HOURS} hours before it started.`,
  },
  night_before_classes: {
    label: 'Classes prepared the night before',
    unit: 'class_occurrences',
    definition:
      'Distinct class occurrences starting in the selected range with at least one night-before action by a tutor acting as themselves.',
  },
  unprompted_night_before_classes: {
    label: 'Classes prepared the night before, unprompted',
    unit: 'class_occurrences',
    definition: `Classes prepared the night before by a tutor with no logged nudge in the ${NUDGE_LOOKBACK_HOURS} hours before the class started.`,
  },
  remind_copies: {
    label: 'Remind copies',
    unit: 'copy_actions',
    definition:
      'Homework reminders tutors copied or shared, acting as themselves, in the selected range. A copy does not prove the message was sent.',
  },
  photo_request_copies: {
    label: 'Ask for photo copies',
    unit: 'copy_actions',
    definition:
      'Working-photo requests tutors copied, acting as themselves, in the selected range. A copy does not prove the message was sent.',
  },
  class_page_nav_opens: {
    label: 'Class page opens · navigation',
    unit: 'page_opens',
    definition:
      'Class page opens from the Students navigation item by tutors acting as themselves. Opens from the sign-in landing never count.',
  },
  class_page_link_opens: {
    label: 'Class page opens · links',
    unit: 'page_opens',
    definition:
      'Class page opens from a link, such as a My Classes row or a homework, by tutors acting as themselves. Opens from the sign-in landing never count.',
  },
  minutes_to_first_copy: {
    label: 'Minutes from class page open to first copy',
    unit: 'minutes',
    definition: `Median minutes from a navigation or link class page open to the same tutor’s first Remind or Ask for photo copy for that class, when the copy follows within ${COPY_AFTER_OPEN_WINDOW_MINUTES} minutes and before the tutor opens that class page again. Opens without such a copy are not in the sample.`,
  },
  nudges_logged: {
    label: 'Nudges logged',
    unit: 'nudges',
    definition: `Nudges an administrator logged in this report for tutors in scope during the selected range. A tutor’s classes starting within ${NUDGE_LOOKBACK_HOURS} hours after a nudge count as prompted.`,
  },
  homework_answered_uptake: {
    label: 'Homework answered within 7 days',
    unit: 'percent',
    definition:
      'Eligible publication student–homework pairs with a genuine answer in the exact seven elapsed days after publication. Headline rates include fully observed cohorts only.',
  },
  homework_submitted_uptake: {
    label: 'Homework submitted within 7 days',
    unit: 'percent',
    definition:
      'Eligible publication student–homework pairs submitted in the exact seven elapsed days after publication. Empty submissions can count independently of genuine answers.',
  },
  homework_before_class: {
    label: 'Homework submitted before class',
    unit: 'percent',
    definition:
      'Eligible publication student–homework pairs in mainstream live classes, published in the range, submitted before the class the homework precedes: the first class starting at or after its due time, or after publication when undated, as the class schedule records it (moved and added sessions included). Counted once that class has started; a recipient with no submission by then is not before class. Assignment groups and nonprofit classes are left out.',
  },
  homework_before_class_photos_required: {
    label: 'Submitted before class · photos required',
    unit: 'percent',
    definition:
      'Homework submitted before class, for homework that requires working photos as it is set now. Compare it with the photos-optional rate to see whether requiring photos costs completion.',
  },
  homework_before_class_photos_optional: {
    label: 'Submitted before class · photos optional',
    unit: 'percent',
    definition:
      'Homework submitted before class, for homework where working photos are optional as it is set now, including homework from before the setting existed.',
  },
  working_photo_optional: {
    label: 'Submissions with working photos · optional',
    unit: 'percent',
    definition:
      'Share of submissions to homework published in the selected range, made while working photos were optional, that carried at least one working photo, both as frozen at submit.',
  },
  weekly_working_photos: {
    label: 'Working photos · 7 days',
    unit: 'photos',
    definition:
      'Working photos on submissions to homework published in the seven reporting days ending on the reference day, as frozen at submit: a photo on two questions counts once, and a photo added after submitting is not counted. Submissions recorded so far; a late submission adds to the week its homework was published.',
  },
  original_cohort: {
    label: 'Original cohort',
    unit: 'students',
    definition:
      'The original eligible cohort entering in this Monday-based reporting week, using its full entry week.',
  },
  returning_cohort: {
    label: 'Returning cohort',
    unit: 'students',
    definition:
      'Members of the original cohort returning in the selected complete reporting week after their entry week.',
  },
} as const;
export type AnalyticsMetricId = keyof typeof PRODUCT_ANALYTICS_METRICS;

/**
 * What a rate's matching-people detail counts: the units the rate is taken
 * over. The open-to-copy median's detail counts the timed opens.
 */
const DETAIL_UNITS: Partial<Record<AnalyticsMetricId, AnalyticsCountedUnit>> = {
  night_before_teachers: 'class_occurrences',
  unprompted_night_before_teachers: 'class_occurrences',
  minutes_to_first_copy: 'page_opens',
  homework_answered_uptake: 'student_homework_pairs',
  homework_submitted_uptake: 'student_homework_pairs',
  homework_before_class: 'student_homework_pairs',
  homework_before_class_photos_required: 'student_homework_pairs',
  homework_before_class_photos_optional: 'student_homework_pairs',
  working_photo_optional: 'student_homework_pairs',
};

/** The unit a metric's matching-people detail counts; a count counts its own. */
export function analyticsDetailUnit(
  metric: AnalyticsMetricId
): AnalyticsCountedUnit {
  return DETAIL_UNITS[metric] ?? PRODUCT_ANALYTICS_METRICS[metric].unit;
}

export interface AnalyticsInterval {
  from: string;
  to: string;
  startInclusive: string;
  endExclusive: string;
  referenceDay: string;
  includesToday: boolean;
  timezone: 'Asia/Singapore';
}

export interface AnalyticsCoverageGap {
  from: string;
  to: string | null;
  reason: string;
}
export interface AnalyticsSourceCoverage {
  source: AnalyticsSource;
  schemaReady: boolean;
  completeFrom: string | null;
  observedFrom: string | null;
  gaps: AnalyticsCoverageGap[];
  status: AnalyticsMetricStatus;
  reason: string | null;
}
export interface AnalyticsMetric {
  unit: AnalyticsCountedUnit;
  value: number | null;
  sampleSize: number;
  numerator?: number | null;
  denominator?: number | null;
  status: AnalyticsMetricStatus;
  reason: string | null;
  window: { from: string; to: string };
  observableAfter?: string | null;
  observedSubset?: string | null;
}
export interface AnalyticsMetadata {
  definitionVersion: typeof PRODUCT_ANALYTICS_DEFINITION_VERSION;
  asOf: string;
  interval: AnalyticsInterval;
  membership: { label: 'Current cohort membership'; resolvedAt: string };
  exclusionsChangedAt: string | null;
  excludedAccountCount: number;
  coverage: AnalyticsSourceCoverage[];
  limitations: string[];
}
export interface AnalyticsTrendPoint {
  day: string;
  metrics: Partial<Record<AnalyticsMetricId, AnalyticsMetric>>;
}
export interface AnalyticsSelectionBreakdown {
  category: AnalyticsSelectionCategory;
  answers: AnalyticsMetric;
  students: AnalyticsMetric;
}
export interface AnalyticsRetentionCell extends AnalyticsMetric {
  week: 1 | 2 | 3 | 4;
}
export interface AnalyticsRetentionCohort {
  cohortStart: string;
  population: AnalyticsPopulation;
  kind: 'student' | 'teacher';
  originalSize: number;
  entryStatus: AnalyticsMetricStatus;
  observableAfter: string;
  weeks: AnalyticsRetentionCell[];
}
export interface AnalyticsPopulationReport {
  population: AnalyticsPopulation;
  sample: { students: number; teachers: number };
  metrics: Partial<Record<AnalyticsMetricId, AnalyticsMetric>>;
  series: AnalyticsTrendPoint[];
  selection: AnalyticsSelectionBreakdown[];
  retention: AnalyticsRetentionCohort[];
  unknownEligibilityCount: number;
  unattributedPublications: number;
}
export interface ProductAnalyticsReport {
  metadata: AnalyticsMetadata;
  /** Compare returns Regular and Nonprofit in that order; All is a deduped union. */
  populations: AnalyticsPopulationReport[];
  union?: AnalyticsPopulationReport;
}
export interface AnalyticsFilterOption {
  id: string;
  label: string;
}
export interface ProductAnalyticsFilters {
  organizations: AnalyticsFilterOption[];
  cohorts: Array<
    AnalyticsFilterOption & { organizationId: string | null; legacy: boolean }
  >;
  resolvedAt: string;
  excludedAccountCount: number;
}
export interface AnalyticsAccount {
  userId: string;
  displayName: string;
  email: string;
  role: string;
  accountStatus: string;
  population: Exclude<AnalyticsPopulation, 'all'>;
  organizationIds: string[];
  cohortIds: string[];
  excluded: boolean;
}
export interface AnalyticsUserRow extends AnalyticsAccount {
  count: number;
  unit: AnalyticsCountedUnit;
  firstAt: string | null;
  lastAt: string | null;
  eligibleCount?: number;
  /** Open-to-copy detail: this tutor's median over the `count` timed opens. */
  medianMinutes?: number;
}
export interface AnalyticsPage<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
}
export interface ProductAnalyticsUsers extends AnalyticsPage<AnalyticsUserRow> {
  metadata: AnalyticsMetadata;
  metric: AnalyticsMetricId;
  unit: AnalyticsCountedUnit;
  /** Materials/actions can exceed the number of distinct actor rows. */
  matchingUnits: number;
}
export interface AnalyticsUptakePair {
  student: AnalyticsAccount;
  homeworkId: string;
  homeworkTitle: string;
  publishedAt: string;
  observableAfter: string;
  answered: boolean;
  submitted: boolean;
  status: AnalyticsMetricStatus;
}
export interface ProductAnalyticsUptake
  extends AnalyticsPage<AnalyticsUptakePair | AnalyticsUserRow> {
  metadata: AnalyticsMetadata;
  view: 'pairs' | 'students';
  unit: 'student_homework_pairs' | 'students';
  eligiblePairs: number;
  qualifyingPairs: number;
}
export interface AnalyticsExclusionHistoryEntry {
  actorUserId: string;
  previousExcluded: boolean;
  excluded: boolean;
  reason: string;
  changedAt: string;
}
export interface AnalyticsExclusion {
  account: AnalyticsAccount;
  excluded: boolean;
  /** Null for an initial no-op restore: no setting change ever occurred. */
  updatedAt: string | null;
  reason: string | null;
}
/** One row per commanded account, in the order the command named them. */
export interface AnalyticsExclusionCommandResult {
  rows: AnalyticsExclusion[];
}
export type AnalyticsExclusionCandidates = AnalyticsPage<AnalyticsAccount>;
/** Real tenant accounts with teaching evidence, independent of report filters. */
export type AnalyticsNudgeCandidates = AnalyticsPage<AnalyticsAccount>;
/** `POST /teacher-nudges`: the admin nudge row the report now counts. */
export interface AnalyticsTeacherNudge {
  teacherUserId: string;
  classCodeId: string | null;
  loggedAt: string;
}
export type AnalyticsExclusionList = AnalyticsPage<AnalyticsExclusion>;
export interface AnalyticsExclusionHistory
  extends AnalyticsPage<AnalyticsExclusionHistoryEntry> {
  userId: string;
}
export interface AnalyticsDemoPosition {
  position: number;
  eligibleRuns: number;
  shown: number;
  answered: number;
  continuation: AnalyticsMetric | null;
  technicalObservations: number;
}
export interface ProductAnalyticsDemo {
  metadata: AnalyticsMetadata;
  runsStarted: AnalyticsMetric;
  browsersStarting: AnalyticsMetric;
  browsersAttempting: AnalyticsMetric;
  positions: AnalyticsDemoPosition[];
  completed: AnalyticsMetric;
  resultsViewed: AnalyticsMetric;
  inProgress: AnalyticsMetric;
  technicalExits: AnalyticsMetric;
  behavioralExits: AnalyticsMetric;
  completionAmongClosed: AnalyticsMetric;
}
